import { useEffect, useId, useRef, useState } from 'react';

/**
 * Aufklappbarer Bereich auf Basis des nativen `popover`-Attributs.
 *
 * Wie beim Dialog bringt die Plattform mit, was sonst mühsam nachzubauen wäre:
 * Ein Klick daneben schließt, Escape schließt, und der Inhalt liegt in der
 * obersten Ebene — er wird also von nichts überdeckt, was im Chart darunter
 * gezeichnet wird.
 *
 * Nur die Platzierung bleibt zu tun. Ein Popover in der obersten Ebene lässt sich
 * nicht mit `position: absolute` an einem Vorfahren ausrichten; die Koordinaten
 * werden deshalb beim Öffnen aus dem Knopf abgeleitet.
 */

interface PopoverProps {
  /** Aufschrift des Knopfs. */
  label: React.ReactNode;
  title?: string;
  /** Klasse des Knopfs, damit er auch als Farbtupfer auftreten kann. */
  className?: string;
  /** Beschriftung für Vorlesesoftware, wenn die Aufschrift nur ein Bild ist. */
  ariaLabel?: string;
  /**
   * Zusätzliche Klasse für die aufgeklappte Tafel.
   *
   * Nötig, weil die Breite vom Inhalt abhängt: Eine Liste von Indikatoren braucht
   * Platz, eine Farbpalette ist ein paar Kästchen breit. Eine gemeinsame Breite
   * ließe entweder die Liste zu eng oder die Palette absurd leer.
   */
  panelClassName?: string;
  /**
   * Der Inhalt. Als Funktion aufgerufen, wenn er selbst schließen können muss —
   * etwa eine Palette, die sich nach der Wahl einer Farbe zurückzieht.
   */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
}

export const Popover = ({
  label,
  title,
  className,
  ariaLabel,
  panelClassName,
  children,
}: PopoverProps): React.JSX.Element => {
  const id = `popover-${useId().replace(/:/g, '')}`;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger) return;

    const platzieren = (): void => {
      const box = trigger.getBoundingClientRect();
      panel.style.top = `${box.bottom + 6}px`;
      /*
       * Rechtsbündig zum Knopf, aber nie über den linken Rand hinaus. Ohne die
       * untere Schranke ragte ein breites Popover auf einem schmalen Fenster aus
       * dem Bild — und dort ist es nicht erreichbar, weil die oberste Ebene nicht
       * mitrollt.
       */
      panel.style.left = `${Math.max(8, box.right - panel.offsetWidth)}px`;
    };

    const beiWechsel = (event: Event): void => {
      const offen = (event as ToggleEvent).newState === 'open';
      setIsOpen(offen);
      if (offen) platzieren();
    };

    panel.addEventListener('toggle', beiWechsel);
    /* Beim Verändern des Fensters wandert der Knopf; das Popover muss folgen. */
    window.addEventListener('resize', platzieren);

    return () => {
      panel.removeEventListener('toggle', beiWechsel);
      window.removeEventListener('resize', platzieren);
    };
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        popoverTarget={id}
        aria-expanded={isOpen}
        {...(className ? { className } : {})}
        {...(title ? { title } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      >
        {label}
      </button>

      <div
        ref={panelRef}
        id={id}
        popover="auto"
        className={`popover${panelClassName ? ` ${panelClassName}` : ''}`}
      >
        {typeof children === 'function'
          ? children(() => {
              /*
               * Abgesichert aufgerufen: Wo die Plattform `popover` nicht kennt,
               * fehlt auch diese Methode. Ein Fehler beim Schließen wäre dann
               * schlimmer als ein Popover, das offen bleibt — und in der
               * Testumgebung gibt es sie ohnehin nicht.
               */
              panelRef.current?.hidePopover?.();
            })
          : children}
      </div>
    </>
  );
};
